/**
 * ZeoComposer - grosse abgerundete Eingabeleiste unten (Mittwald-Playground-
 * Optik). Chat-Anhaenge (08.10.2026): Büroklammer (Dateiauswahl), Strg+V (Screenshot), Drag&Drop (ueber `ref.addFiles` vom
 * Seitencontainer). Anhaenge werden sofort hochgeladen (lokale Textextraktion), vor dem Senden mit Vorschau angezeigt und
 * mit dem Text zu EINER Nachricht gesendet. Ein Chat-Anhang ist KEIN Akten-Dokument.
 * Sprache nutzt spaeter denselben ZEO/Qwen-Kontext + ElevenLabs.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Send, Paperclip, Mic, Loader2, X, Reply, FileText, AlertTriangle } from 'lucide-react';
import { betreuungAPI } from '../../api/betreuungAPI';
import { ACCEPT, MAX_ANZAHL, dateienAusClipboard, formatGroesse, istBild, pruefeClientDatei, typLabel } from './zeoAnhang';

const ZeoComposer = forwardRef(function ZeoComposer({
  onSend, loading, onVoiceClick, replyTarget, onClearReply,
  selectedDocuments, onRemoveSelectedDocument,
}, ref) {
  const [text, setText] = useState('');
  const [anhaenge, setAnhaenge] = useState([]);       // {lid, name, groesse, typ, istBild, vorschau, status: 'laedt'|'ok'|'fehler', id, extraktion, fehler}
  const [hinweis, setHinweis] = useState('');
  const dateiInput = useRef(null);
  const anhaengeRef = useRef([]);
  anhaengeRef.current = anhaenge;

  const addFiles = useCallback(async (dateien) => {
    if (!dateien || dateien.length === 0) return;
    const platz = MAX_ANZAHL - anhaengeRef.current.length;
    const fehler = [];
    const gueltig = [];
    dateien.forEach((f) => {
      const p = pruefeClientDatei(f);
      if (!p.ok) fehler.push(p.grund); else gueltig.push(f);
    });
    if (gueltig.length > platz) fehler.push(`Maximal ${MAX_ANZAHL} Anhänge je Nachricht.`);
    const nehmen = gueltig.slice(0, Math.max(platz, 0));
    setHinweis(fehler.join(' '));
    if (nehmen.length === 0) return;
    const neu = nehmen.map((f) => ({
      lid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: f.name, groesse: f.size, typ: typLabel(f), istBild: istBild(f),
      vorschau: istBild(f) && /^(image\/(png|jpeg|webp))$/.test(f.type) ? URL.createObjectURL(f) : null, status: 'laedt', file: f,
    }));
    setAnhaenge((prev) => [...prev, ...neu]);
    try {
      const res = await betreuungAPI.zeoAnhaengeHochladen(nehmen);
      setAnhaenge((prev) => prev.map((a) => {
        const i = neu.findIndex((n) => n.lid === a.lid);
        if (i < 0) return a;
        const r = (res.anhaenge || [])[i] || {};
        return r.ok ? { ...a, status: 'ok', id: r.id, extraktion: r.extraktion, file: undefined } : { ...a, status: 'fehler', fehler: r.fehler || 'Upload fehlgeschlagen', file: undefined };
      }));
    } catch (e) {
      setAnhaenge((prev) => prev.map((a) => (neu.some((n) => n.lid === a.lid) ? { ...a, status: 'fehler', fehler: e.message || 'Upload fehlgeschlagen', file: undefined } : a)));
    }
  }, []);

  useImperativeHandle(ref, () => ({ addFiles }), [addFiles]);

  const entferne = async (a) => {
    setAnhaenge((prev) => prev.filter((x) => x.lid !== a.lid));
    if (a.vorschau) URL.revokeObjectURL(a.vorschau);
    if (a.id) { try { await betreuungAPI.zeoAnhangVerwerfen(a.id); } catch { /* serverseitig ohnehin verwaist-bereinigt */ } }
  };

  useEffect(() => () => { anhaengeRef.current.forEach((a) => a.vorschau && URL.revokeObjectURL(a.vorschau)); }, []);

  const laedt = anhaenge.some((a) => a.status === 'laedt');
  const okAnhaenge = anhaenge.filter((a) => a.status === 'ok');
  const kannSenden = (text.trim() || okAnhaenge.length > 0) && !loading && !laedt;

  const senden = () => {
    if (!kannSenden) return;
    const wert = text.trim();
    const mitAnhang = okAnhaenge.map((a) => ({ id: a.id, name: a.name, groesse: a.groesse, art: a.istBild ? 'bild' : null, typ: a.typ, extraktion: a.extraktion, vorschau: a.vorschau }));
    setText('');
    setAnhaenge([]);   // gesendete Anhaenge verlassen den Composer (Vorschau-URL bleibt der Nachricht)
    setHinweis('');
    onSend(wert, {
      replyToMessageId: replyTarget?.message_id || null,
      replyToBlockId: replyTarget?.block_id || null,
      selectedDocumentIds: (selectedDocuments || []).map((d) => d.id),
      anhaenge: mitAnhang,
    });
  };

  const onPaste = (e) => {
    const dateien = dateienAusClipboard(e.clipboardData);
    if (dateien.length === 0) return;      // reiner Text: normales Einfuegen
    e.preventDefault();
    addFiles(dateien);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      senden();
    }
  };

  return (
    <div className="shrink-0 px-6 pb-6 pt-2">
      <div className="mx-auto w-full max-w-[min(100%,96rem)]">
        {(anhaenge.length > 0 || hinweis) && (
          <div className="mb-2 flex flex-col gap-1.5" data-testid="zeo-anhang-vorschau">
            {hinweis && (
              <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 w-fit" data-testid="zeo-anhang-hinweis">
                <AlertTriangle size={12} /> {hinweis}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {anhaenge.map((a) => (
                <div key={a.lid} className={`flex items-center gap-2 text-xs rounded-xl border px-2.5 py-1.5 bg-white ${a.status === 'fehler' ? 'border-red-200' : 'border-violet-100'}`} data-testid="zeo-anhang-chip" data-status={a.status}>
                  {a.vorschau ? (
                    <img src={a.vorschau} alt="" className="w-10 h-10 rounded object-cover" data-testid="zeo-anhang-thumb" />
                  ) : (
                    <FileText size={18} className="text-violet-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-slate-700 truncate max-w-[220px]" data-testid="zeo-anhang-name">{a.name}</div>
                    <div className="text-slate-400" data-testid="zeo-anhang-info">
                      {a.typ} · {formatGroesse(a.groesse)}
                      {a.status === 'laedt' && ' · wird hochgeladen…'}
                      {a.status === 'ok' && a.extraktion !== 'ok' && ' · kein Text erkannt'}
                      {a.status === 'fehler' && <span className="text-red-600"> · {a.fehler}</span>}
                    </div>
                  </div>
                  {a.status === 'laedt' && <Loader2 size={12} className="animate-spin text-slate-400" />}
                  <button type="button" onClick={() => entferne(a)} className="text-slate-400 hover:text-slate-600 shrink-0" title="Anhang entfernen" data-testid="zeo-anhang-entfernen">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {(replyTarget || (selectedDocuments && selectedDocuments.length > 0)) && (
          <div className="mb-2 flex flex-col gap-1.5" data-testid="zeo-composer-context">
            {replyTarget && (
              <div className="flex items-center gap-2 text-xs bg-violet-50 border border-violet-100 rounded-lg px-3 py-1.5 w-fit max-w-full">
                <Reply size={12} className="text-violet-500 shrink-0" />
                <span className="text-slate-500 shrink-0">Antwort auf:</span>
                <span className="text-slate-700 truncate">{replyTarget.ausschnitt}</span>
                <button type="button" onClick={onClearReply} className="text-slate-400 hover:text-slate-600 shrink-0" data-testid="zeo-reply-clear">
                  <X size={12} />
                </button>
              </div>
            )}
            {selectedDocuments && selectedDocuments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-1.5 text-xs bg-violet-50 border border-violet-100 rounded-lg px-2.5 py-1">
                    <FileText size={11} className="text-violet-500" />
                    <span className="text-slate-700 truncate max-w-[180px]">{doc.titel || doc.id}</span>
                    <button type="button" onClick={() => onRemoveSelectedDocument?.(doc.id)} className="text-slate-400 hover:text-slate-600" data-testid="zeo-selected-document-remove">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div
          className="flex items-end gap-2 bg-white rounded-3xl border border-violet-100 shadow-sm px-4 py-3"
          data-testid="zeo-composer"
        >
          <button
            type="button"
            className="p-2 rounded-full text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors shrink-0"
            title="Datei anhängen (PDF, Bild, Text)"
            onClick={() => dateiInput.current?.click()}
            data-testid="zeo-composer-attach"
          >
            <Paperclip size={18} />
          </button>
          <input
            ref={dateiInput}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            data-testid="zeo-composer-file-input"
            onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
          />

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder="Frage ZEO etwas…"
            rows={1}
            className="flex-1 resize-none bg-transparent outline-none text-[15px] leading-relaxed max-h-40 py-1.5 placeholder:text-slate-400"
            data-testid="zeo-composer-input"
          />

          <button
            type="button"
            onClick={onVoiceClick}
            className="p-2 rounded-full text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors shrink-0"
            title="Spracheingabe (demnächst)"
            disabled={!onVoiceClick}
            data-testid="zeo-composer-voice"
          >
            <Mic size={18} />
          </button>

          <button
            type="button"
            onClick={senden}
            disabled={!kannSenden}
            className="p-2.5 rounded-full bg-violet-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-violet-700 transition-colors shrink-0"
            data-testid="zeo-composer-send"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
        <p className="text-center text-[11px] text-slate-300 mt-2">
          ZEO kann Fehler machen. Wichtige Angaben bitte prüfen.
        </p>
      </div>
    </div>
  );
});

export default ZeoComposer;
